import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Alert,
  CircularProgress,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

import { auditService } from '../../services/auditService';
import {
  DocumentAuditLog,
  DocumentAuditHistoryRequest,
  AUDIT_ACTION_LABELS,
  AUDIT_STATUS_COLORS
} from '../../types/audit';

interface DocumentAuditHistoryProps {
  documentId: string;
  documentType?: string;
  title?: string;
  maxHeight?: number;
}

const DocumentAuditHistory: React.FC<DocumentAuditHistoryProps> = ({
  documentId,
  documentType,
  title = 'Audit History',
  maxHeight = 400
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [auditLogs, setAuditLogs] = useState<DocumentAuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 10;

  // Filters
  const [filters, setFilters] = useState<Omit<DocumentAuditHistoryRequest, 'document_id'>>({
    operation: '',
    admin_id: '',
    start_date: '',
    end_date: '',
    limit,
    offset: 0
  });

  useEffect(() => {
    loadAuditHistory();
  }, [documentId, page, filters.operation, filters.admin_id, filters.start_date, filters.end_date]);

  const loadAuditHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        ...filters,
        document_type: documentType,
        limit,
        offset: (page - 1) * limit
      };

      const response = await auditService.getDocumentAuditHistory(documentId, params);
      
      setAuditLogs(response.audit_logs);
      setTotalRecords(response.total);
      setTotalPages(Math.ceil(response.total / limit));
    } catch (err: any) {
      console.error('Failed to load audit history:', err);
      setError('Failed to load audit history');
      enqueueSnackbar('Failed to load audit history', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadAuditHistory();
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const toggleRowExpansion = (logId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedRows(newExpanded);
  };

  const handleFilterChange = (field: keyof typeof filters, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      operation: '',
      admin_id: '',
      start_date: '',
      end_date: '',
      limit,
      offset: 0
    });
    setPage(1);
  };

  const formatTimestamp = (timestamp: string) => {
    return format(new Date(timestamp), 'dd/MM/yyyy HH:mm:ss');
  };

  const getActionLabel = (action: string) => {
    return AUDIT_ACTION_LABELS[action] || action.charAt(0).toUpperCase() + action.slice(1);
  };

  const getStatusColor = (status: string) => {
    return AUDIT_STATUS_COLORS[status] || 'default';
  };

  const renderDetails = (details: Record<string, any>) => {
    if (!details || Object.keys(details).length === 0) {
      return <Typography variant="body2" color="text.secondary">No additional details</Typography>;
    }

    return (
      <Box>
        {Object.entries(details).map(([key, value]) => (
          <Box key={key} sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
            </Typography>
            <Typography variant="body2" sx={{ ml: 1 }}>
              {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  if (loading && auditLogs.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">{title}</Typography>
          <Box display="flex" gap={1}>
            <Tooltip title="Toggle Filters">
              <IconButton onClick={() => setShowFilters(!showFilters)} size="small">
                <FilterIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={handleRefresh} size="small" disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Filters */}
        <Collapse in={showFilters}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Operation</InputLabel>
                    <Select
                      value={filters.operation}
                      onChange={(e) => handleFilterChange('operation', e.target.value)}
                      label="Operation"
                    >
                      <MenuItem value="">All Operations</MenuItem>
                      <MenuItem value="create">Create</MenuItem>
                      <MenuItem value="update">Update</MenuItem>
                      <MenuItem value="delete">Delete</MenuItem>
                      <MenuItem value="convert">Convert</MenuItem>
                      <MenuItem value="send">Send</MenuItem>
                      <MenuItem value="approve">Approve</MenuItem>
                      <MenuItem value="reject">Reject</MenuItem>
                      <MenuItem value="cancel">Cancel</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Start Date"
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => handleFilterChange('start_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="End Date"
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => handleFilterChange('end_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    onClick={clearFilters}
                    size="small"
                    fullWidth
                  >
                    Clear Filters
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Collapse>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {auditLogs.length === 0 && !loading ? (
          <Alert severity="info">
            No audit records found for this document.
          </Alert>
        ) : (
          <>
            <TableContainer sx={{ maxHeight }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="40px"></TableCell>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actor</TableCell>
                    <TableCell>IP Address</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditLogs.map((log) => (
                    <React.Fragment key={log.id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleRowExpansion(log.id)}
                          >
                            {expandedRows.has(log.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatTimestamp(log.timestamp)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {getActionLabel(log.action)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {log.event_type}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.status.toUpperCase()}
                            color={getStatusColor(log.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {log.actor_type === 'admin' ? 'Admin' : log.actor_type}
                          </Typography>
                          {log.actor_id && (
                            <Typography variant="caption" color="text.secondary">
                              ID: {log.actor_id}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {log.ip_address || 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0 }}>
                          <Collapse in={expandedRows.has(log.id)}>
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Details
                                  </Typography>
                                  {renderDetails(log.details)}
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Technical Information
                                  </Typography>
                                  <Box>
                                    {log.request_id && (
                                      <Typography variant="caption" display="block">
                                        Request ID: {log.request_id}
                                      </Typography>
                                    )}
                                    {log.user_agent && (
                                      <Typography variant="caption" display="block">
                                        User Agent: {log.user_agent}
                                      </Typography>
                                    )}
                                    {log.error_message && (
                                      <Alert severity="error" sx={{ mt: 1 }}>
                                        {log.error_message}
                                      </Alert>
                                    )}
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Showing {auditLogs.length} of {totalRecords} records
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DocumentAuditHistory;